import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
    FiClock,
    FiUsers,
    FiLogIn,
    FiLogOut,
    FiFilter,
    FiRefreshCw,
} from "react-icons/fi";
import { toast } from "react-hot-toast";
import axiosInstance from "../utils/api";
import Navbar from "./Navbar";

const TABS = [
    { key: "stand_builder", label: "Stand Builder" },
    { key: "contractor", label: "Contractors" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const StatCard = ({ icon, label, value, accent }) => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${accent}`}>
            {icon}
        </div>
        <div>
            <p className="text-xs text-slate-500 font-medium">{label}</p>
            <p className="text-xl font-bold text-slate-800">{value}</p>
        </div>
    </div>
);

const BadgesHistory = (eventId) => {    
    const [activeTab, setActiveTab] = useState("stand_builder");
    const [loading, setLoading] = useState(true);
    const [entries, setEntries] = useState([]);
    const [totalBadges, setTotalBadges] = useState(0);
    const [onSiteCount, setOnSiteCount] = useState(0);

    const [chefs, setChefs] = useState([]);
    const [filters, setFilters] = useState({
        chefId: "",
        startDate: "",
        endDate: "",
        startHour: "",
        endHour: "",
    });

    const fetchChefs = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get("/badge/history/chefs", {
                params: { eventId: eventId.eventId, category: activeTab },
            });
            setChefs(data.chefs || []);
            console.log(data.chefs);
            
        } catch (err) {
            // non-fatal — filter dropdown just stays empty
        }
    }, [eventId, activeTab]);

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axiosInstance.get("/badge/history", {
                params: {
                    eventId: eventId.eventId,
                    category: activeTab,
                    chefId: filters.chefId || undefined,
                    startDate: filters.startDate || undefined,
                    endDate: filters.endDate || undefined,
                    startHour: filters.startHour !== "" ? filters.startHour : undefined,
                    endHour: filters.endHour !== "" ? filters.endHour : undefined,
                },
            });
            setEntries(data.entries || []);
            setTotalBadges(data.totalBadges || 0);
            setOnSiteCount(data.onSiteCount || 0);
        } catch (err) {
            toast.error("Erreur lors du chargement de l'historique");
        } finally {
            setLoading(false);
        }
    }, [eventId, activeTab, filters]);

    useEffect(() => {
        fetchChefs();
    }, [fetchChefs]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const resetFilters = () => {
        setFilters({ chefId: "", startDate: "", endDate: "", startHour: "", endHour: "" });
    };

    const formatDateTime = (d) =>
        new Date(d).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });

    return (
        <>
            <div className="min-h-screen">
                <div className="mx-auto">

                    <div className="mb-5">
                        <h1 className="text-2xl sm:text-3xl font-bold text-blue-900">Historique des badges</h1>
                        <p className="text-sm text-slate-500 mt-1">Entrées / sorties et présence sur site</p>
                    </div>

                    {/* TABS */}
                    <div className="flex gap-2 mb-5">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-4 py-2.5 rounded-2xl text-sm font-semibold border transition ${activeTab === tab.key
                                    ? "bg-blue-900 border-blue-900 text-white"
                                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* STATS */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                        <StatCard
                            icon={<FiUsers className="text-emerald-700" size={18} />}
                            label="Actuellement sur site"
                            value={onSiteCount}
                            accent="bg-emerald-50"
                        />
                        <StatCard
                            icon={<FiClock className="text-blue-900" size={18} />}
                            label="Badges dans cette catégorie"
                            value={totalBadges}
                            accent="bg-blue-50"
                        />
                        <StatCard
                            icon={<FiFilter className="text-amber-700" size={18} />}
                            label="Scans affichés"
                            value={entries.length}
                            accent="bg-amber-50"
                        />
                    </div>

                    {/* FILTERS */}
                    <div className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-5 shadow-sm mb-5">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Chef / Manager</label>
                                <select
                                    value={filters.chefId}
                                    onChange={(e) => setFilters((p) => ({ ...p, chefId: e.target.value }))}
                                    className="w-full h-11 rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-900 focus:ring-4 focus:ring-blue-100"
                                >
                                    <option value="">Tous</option>
                                    {chefs.map((c) => (
                                        <option key={c._id} value={c._id}>
                                            {c.name} {c.cin ? `(${c.cin})` : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Date début</label>
                                <input
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))}
                                    className="w-full h-11 rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-900 focus:ring-4 focus:ring-blue-100"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Date fin</label>
                                <input
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))}
                                    className="w-full h-11 rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-900 focus:ring-4 focus:ring-blue-100"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Heure début</label>
                                <select
                                    value={filters.startHour}
                                    onChange={(e) => setFilters((p) => ({ ...p, startHour: e.target.value }))}
                                    className="w-full h-11 rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-900 focus:ring-4 focus:ring-blue-100"
                                >
                                    <option value="">--</option>
                                    {HOURS.map((h) => (
                                        <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Heure fin</label>
                                <select
                                    value={filters.endHour}
                                    onChange={(e) => setFilters((p) => ({ ...p, endHour: e.target.value }))}
                                    className="w-full h-11 rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-900 focus:ring-4 focus:ring-blue-100"
                                >
                                    <option value="">--</option>
                                    {HOURS.map((h) => (
                                        <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end mt-3">
                            <button
                                onClick={resetFilters}
                                className="h-10 px-4 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium flex items-center gap-2 transition"
                            >
                                <FiRefreshCw size={14} />
                                Réinitialiser
                            </button>
                        </div>
                    </div>

                    {/* TABLE */}
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[900px]">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        {["Badge", "CIN", "Chef / Manager", "Gate", "Action", "Agent", "Horodatage"].map((title) => (
                                            <th
                                                key={title}
                                                className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                                            >
                                                {title}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={7} className="py-16 text-center text-slate-400">
                                                Chargement...
                                            </td>
                                        </tr>
                                    ) : entries.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="py-16 text-center text-slate-400">
                                                Aucun scan trouvé pour ces filtres
                                            </td>
                                        </tr>
                                    ) : (
                                        entries.map((entry, i) => (
                                            <tr
                                                key={`${entry.badgeId}-${entry.scannedAt}-${i}`}
                                                className="border-b border-slate-100 hover:bg-slate-50 transition"
                                            >
                                                <td className="px-5 py-4 text-sm font-semibold text-slate-800">
                                                    {entry.badgeName}
                                                </td>
                                                <td className="px-5 py-4 text-sm text-slate-600">{entry.cin}</td>
                                                <td className="px-5 py-4 text-sm text-slate-600">{entry.chefName}</td>
                                                <td className="px-5 py-4 text-sm text-slate-600">{entry.gate}</td>
                                                <td className="px-5 py-4">
                                                    <span
                                                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-semibold ${entry.action === "entry"
                                                            ? "bg-emerald-50 text-emerald-700"
                                                            : "bg-amber-50 text-amber-700"
                                                            }`}
                                                    >
                                                        {entry.action === "entry" ? <FiLogIn size={12} /> : <FiLogOut size={12} />}
                                                        {entry.action === "entry" ? "Entrée" : "Sortie"}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-4 text-sm text-slate-500">{entry.agentName}</td>
                                                <td className="px-5 py-4 text-sm text-slate-500">
                                                    {formatDateTime(entry.scannedAt)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BadgesHistory;